/**
 * Enhanced Select Popout functionality for dynamic price updates
 * Updates dropdown prices based on current variant selection
 */

class EnhancedSelectPopout {
  constructor() {
    this.selectors = {
      product: '[data-product]',
      productForm: '[data-product-form]',
      selectPopout: 'popout-select',
      popoutOption: '[data-popout-option]',
      popoutText: '[data-popout-text]',
      optionInput: '[data-single-option-selector]',
      productJson: '[data-product-json]',
      option: '[data-popout-option]',
      popoutToggle: '[data-popout-toggle]',
      variantMetafields: '[data-variants-metafield-json]'
    };

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Listen for product form changes
    document.addEventListener('change', (e) => {
      const optionInput = e.target.closest(this.selectors.optionInput);
      if (optionInput) {
        setTimeout(() => {
          this.updateAllDropdownPrices();
        }, 50);
      }
    });

    // Listen for popout option clicks
    document.addEventListener('click', (e) => {
      const popoutOption = e.target.closest(this.selectors.popoutOption);
      if (popoutOption) {
        // Delay to allow the selection to update
        setTimeout(() => {
          this.updateAllDropdownPrices();
        }, 100);
      }
    });

    // Listen for custom variant change events
    document.addEventListener('theme:variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.updateAllDropdownPricesWithVariant(e.detail.variant);
        this.updateAllButtonsWithVariant(e.detail.variant);
      } else {
        this.updateAllDropdownPrices();
        this.updateAllButtons();
      }
    });

    // Listen for input value changes (for manual input changes)
    document.addEventListener('input', (e) => {
      const optionInput = e.target.closest(this.selectors.optionInput);
      if (optionInput) {
        setTimeout(() => {
          this.updateAllDropdownPrices();
          this.updateAllButtons();
        }, 50);
      }
    });

    // Listen for radio button changes (for swatch/radio button options)
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-single-option-selector]')) {
        setTimeout(() => {
          this.updateAllDropdownPrices();
          this.updateAllButtons();
        }, 50);
      }
    });

    // Listen for Shopify's product form events
    document.addEventListener('variant:change', (e) => {
      setTimeout(() => {
        this.updateAllDropdownPrices();
        this.updateAllButtons();
      }, 100);
    });

    // Listen for popout-select specific events
    document.addEventListener('popout:update', (e) => {
      setTimeout(() => {
        this.updateAllDropdownPrices();
      }, 50);
    });

    // Initial setup with delay to ensure everything is loaded
    setTimeout(() => {
      this.updateAllDropdownPrices();
    }, 500);
  }

  updateDropdownPrices(changedInput = null) {
    const productForms = document.querySelectorAll(this.selectors.productForm);
    
    productForms.forEach(form => {
      this.updateFormDropdownPrices(form, changedInput);
    });
  }

  updateAllDropdownPrices() {
    const productForms = document.querySelectorAll(this.selectors.productForm);
    
    productForms.forEach(form => {
      this.updateFormDropdownPrices(form);
    });
  }

  updateAllDropdownPricesWithVariant(selectedVariant) {
    console.log('Updating dropdowns with selected variant:', selectedVariant);
    const productForms = document.querySelectorAll(this.selectors.productForm);
    
    productForms.forEach(form => {
      this.updateFormDropdownPricesWithVariant(form, selectedVariant);
    });
  }

  updateAllButtons() {
    const productForms = document.querySelectorAll(this.selectors.productForm);
    
    productForms.forEach(form => {
      this.updateFormButtons(form);
    });
  }

  updateAllButtonsWithVariant(selectedVariant) {
    console.log('Updating buttons with selected variant:', selectedVariant);
    const productForms = document.querySelectorAll(this.selectors.productForm);
    
    productForms.forEach(form => {
      this.updateFormButtonsWithVariant(form, selectedVariant);
    });
  }

  updateFormButtons(form) {
    // Find all popout-select buttons and update them based on current selection
    const selectPopouts = form.querySelectorAll(this.selectors.selectPopout);
    
    selectPopouts.forEach(selectPopout => {
      const input = selectPopout.querySelector(this.selectors.optionInput);
      const toggle = selectPopout.querySelector('[data-popout-toggle]');
      
      if (input && toggle && input.value) {
        this.updateButtonFromSelectedOption(selectPopout, input.value);
      }
    });
  }

  updateFormButtonsWithVariant(form, selectedVariant) {
    // Find all popout-select buttons and update them with variant data
    const selectPopouts = form.querySelectorAll(this.selectors.selectPopout);
    
    selectPopouts.forEach(selectPopout => {
      const input = selectPopout.querySelector(this.selectors.optionInput);
      const toggle = selectPopout.querySelector('[data-popout-toggle]');
      
      if (input && toggle && input.value) {
        this.updateButtonFromSelectedOption(selectPopout, input.value);
      }
    });
  }

  updateButtonFromSelectedOption(selectPopout, selectedValue) {
    // Find the selected option element
    const selectedOption = selectPopout.querySelector(`[data-value="${selectedValue}"]`);
    const toggle = selectPopout.querySelector('[data-popout-toggle]');
    
    if (!selectedOption || !toggle) return;
    
    // Get the inner HTML content from the selected option
    const optionContent = selectedOption.innerHTML;
    
    // Find elements in the button to update
    const valueContent = toggle.querySelector('.select-popout__value-content');
    if (!valueContent) return;
    
    // Ensure we have the proper structure with value-main container
    let valueMain = valueContent.querySelector('.select-popout__value-main');
    if (!valueMain) {
      valueMain = document.createElement('div');
      valueMain.classList.add('select-popout__value-main');
      valueContent.appendChild(valueMain);
    }
    
    // Clear existing content first
    valueMain.innerHTML = '';
    
    // Extract content from the selected option
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = optionContent;
    
    const optionLabel = tempDiv.querySelector('.select-popout__option-title') || tempDiv.querySelector('[data-option-title]');
    const optionPrice = tempDiv.querySelector('.select-popout__option-price') || tempDiv.querySelector('[data-option-price]');
    const optionBadge = tempDiv.querySelector('.select-popout__option-badge') || tempDiv.querySelector('[data-option-badge]');
    
    // Create and add label
    const buttonLabel = document.createElement('span');
    buttonLabel.classList.add('select-popout__value-title');
    if (optionLabel) {
      buttonLabel.innerHTML = optionLabel.innerHTML;
    } else {
      // Fallback to selected value if no specific label found
      buttonLabel.textContent = selectedValue;
    }
    valueMain.appendChild(buttonLabel);
    
    // Create and add price if it exists
    if (optionPrice) {
      const buttonPrice = document.createElement('span');
      buttonPrice.classList.add('select-popout__value-price');
      buttonPrice.innerHTML = optionPrice.innerHTML;
      buttonPrice.style.display = 'inline-block';
      valueMain.appendChild(buttonPrice);
    }
    
    // Create and add badge if it exists
    if (optionBadge) {
      const buttonBadge = document.createElement('span');
      buttonBadge.classList.add('select-popout__value-badge');
      buttonBadge.innerHTML = optionBadge.innerHTML;
      buttonBadge.style.display = 'inline-block';
      // Copy any inline styles
      if (optionBadge.style.backgroundColor) {
        buttonBadge.style.backgroundColor = optionBadge.style.backgroundColor;
      }
      valueMain.appendChild(buttonBadge);
    }
  }

  updateFormDropdownPrices(form, changedInput = null) {
    // Try multiple strategies to find the product JSON
    let productJsonElement = form.closest(this.selectors.product)?.querySelector(this.selectors.productJson);
    console.log('Product JSON element found:', productJsonElement);
    // If not found, try looking within the form itself
    if (!productJsonElement) {
      productJsonElement = form.querySelector(this.selectors.productJson);
    }
    
    // If still not found, try looking in siblings
    if (!productJsonElement) {
      const productContainer = form.closest(this.selectors.product);
      if (productContainer) {
        productJsonElement = productContainer.querySelector(this.selectors.productJson);
      }
    }
    
    // Last resort: look for any product JSON in the document near this form
    if (!productJsonElement) {
      const sectionId = form.closest('[data-section-id]')?.getAttribute('data-section-id');
      if (sectionId) {
        productJsonElement = document.querySelector(`#ProductJson-${sectionId}`);
      }
    }
    
    if (!productJsonElement) {
      console.warn('Product JSON element not found. Selectors checked:', {
        product: this.selectors.product,
        productJson: this.selectors.productJson,
        formClosestProduct: form.closest(this.selectors.product),
        formQueryJson: form.querySelector(this.selectors.productJson)
      });
      return;
    }

    let productData;
    try {
      // Get the text content of the script tag, not innerHTML
      const jsonText = productJsonElement.textContent || productJsonElement.innerText;
      productData = JSON.parse(jsonText);
    } catch (e) {
      console.error('Failed to parse product JSON:', e, productJsonElement);
      return;
    }

    // Get all option inputs - including radio buttons, select dropdowns, and hidden inputs
    const optionInputs = this.getAllOptionInputs(form);
    const currentSelections = this.getCurrentSelections(optionInputs);
    
    // Update each dropdown
    console.log('updateFormDropdownPrices - Looking for selectPopouts with selector:', this.selectors.selectPopout);
    const selectPopouts = form.querySelectorAll(this.selectors.selectPopout);
    console.log('updateFormDropdownPrices - Found selectPopouts:', selectPopouts.length);
    selectPopouts.forEach(selectPopout => {
      this.updateSelectPopoutPrices(selectPopout, productData, currentSelections, optionInputs, form);
    });
  }

  getAllOptionInputs(form) {
    // Get all types of option inputs
    const inputsMap = new Map(); // Use Map to avoid duplicate indices
    
    // Hidden inputs for popout selectors
    const hiddenInputs = form.querySelectorAll('input[name^="options["]:not([type="radio"])');
    console.log('Hidden inputs found:', hiddenInputs.length);
    hiddenInputs.forEach(input => {
      const indexMatch = input.name.match(/options\[(\d+)\]/);
      if (indexMatch) {
        const index = parseInt(indexMatch[1]);
        inputsMap.set(index, input);
      }
    });
    
    // Radio buttons (for swatch/box options) - get checked ones
    const radioInputs = form.querySelectorAll('input[name^="options["][type="radio"]:checked');
    console.log('Checked radio inputs found:', radioInputs.length);
    radioInputs.forEach(radio => {
      const indexMatch = radio.name.match(/options\[(\d+)\]/);
      if (indexMatch) {
        const index = parseInt(indexMatch[1]);
        if (!inputsMap.has(index)) { // Don't override hidden inputs
          inputsMap.set(index, radio);
        }
      }
    });
    
    // If no checked radios for some options, get the first radio from each group to establish structure
    const allRadios = form.querySelectorAll('input[name^="options["][type="radio"]');
    const radioGroups = new Set();
    allRadios.forEach(radio => {
      const indexMatch = radio.name.match(/options\[(\d+)\]/);
      if (indexMatch) {
        const index = parseInt(indexMatch[1]);
        if (!inputsMap.has(index) && !radioGroups.has(radio.name)) {
          radioGroups.add(radio.name);
          inputsMap.set(index, radio); // This will have empty value but establishes the structure
        }
      }
    });
    
    // Select dropdowns
    const selectInputs = form.querySelectorAll('select[name^="options["]');
    console.log('Select inputs found:', selectInputs.length);
    selectInputs.forEach(select => {
      const indexMatch = select.name.match(/options\[(\d+)\]/);
      if (indexMatch) {
        const index = parseInt(indexMatch[1]);
        if (!inputsMap.has(index)) { // Don't override other inputs
          inputsMap.set(index, select);
        }
      }
    });
    
    // Convert map to sorted array
    const sortedIndices = Array.from(inputsMap.keys()).sort((a, b) => a - b);
    const inputs = sortedIndices.map(index => inputsMap.get(index));
    
    console.log('Found option inputs by index:', inputs.map((input, i) => ({ 
      index: sortedIndices[i],
      name: input.name, 
      value: input.value, 
      type: input.type,
      checked: input.checked 
    })));
    return inputs;
  }

  getCurrentSelections(optionInputs) {
    const selections = {};
    optionInputs.forEach((input, arrayIndex) => {
      // Get the option name (like "Choose Pack Size", "Choose Ball Colour", etc.)
      const optionName = input.name.replace('options[', '').replace(']', '');
      
      // Extract the actual option index from the input name
      const optionIndexMatch = input.name.match(/options\[(\d+)\]/);
      const optionIndex = optionIndexMatch ? parseInt(optionIndexMatch[1]) : arrayIndex;
      
      // Get the actual selected value
      let selectedValue = null;
      
      if (input.type === 'radio') {
        // For radio buttons, find the checked one in the same group
        const radioGroup = document.querySelectorAll(`input[name="${input.name}"]:checked`);
        if (radioGroup.length > 0) {
          selectedValue = radioGroup[0].value;
        }
      } else if (input.type === 'select-one' || input.tagName === 'SELECT') {
        // For select dropdowns
        selectedValue = input.value;
      } else {
        // For other input types (like hidden inputs for popouts)
        selectedValue = input.value;
      }
      
      // Only include if we have a valid selection
      if (selectedValue && selectedValue.trim() !== '') {
        selections[optionIndex] = {
          name: optionName,
          value: selectedValue
        };
      }
    });
    
    console.log('Current selections with proper indices:', selections);
    return selections;
  }

  updateSelectPopoutPrices(selectPopout, productData, currentSelections, optionInputs, form) {
    // Get variant metafields for badge data
    const variantMetafields = this.getVariantMetafields(form);
    
    const input = selectPopout.querySelector(this.selectors.optionInput);
    if (!input) return;

    const currentOptionIndex = Array.from(optionInputs).indexOf(input);
    const currentOptionName = input.name.replace('options[', '').replace(']', '').toLowerCase();
    
    // Only update dropdowns that are exactly "choose ball"
    if (currentOptionName !== 'choose ball') return;

    const popoutOptions = selectPopout.querySelectorAll(this.selectors.popoutOption);
    const popoutToggle = selectPopout.querySelector('[data-popout-toggle]');

    // Update all dropdown options
    popoutOptions.forEach(option => {
      const optionValue = option.getAttribute('data-value');
      if (!optionValue) return;

      // Create a test selection with this option value
      const testSelections = { ...currentSelections };
      testSelections[currentOptionIndex] = {
        name: currentSelections[currentOptionIndex]?.name || currentOptionName,
        value: optionValue
      };

      // Find matching variant
      const matchingVariant = this.findMatchingVariant(productData, testSelections);
      
      if (matchingVariant) {
        this.updateOptionDisplay(option, matchingVariant, productData, variantMetafields);
      }
    });

    // Update the toggle button display for the currently selected value
    if (popoutToggle) {
      const selectedValue = input.value;
      const testSelections = { ...currentSelections };
      testSelections[currentOptionIndex] = {
        name: currentSelections[currentOptionIndex]?.name || currentOptionName,
        value: selectedValue
      };

      const selectedVariant = this.findMatchingVariant(productData, testSelections);
      if (selectedVariant) {
        this.updateToggleDisplay(popoutToggle, selectedVariant, selectedValue, currentOptionName, productData, variantMetafields);
      }
    }
  }

  findMatchingVariant(productData, selections) {
    // Convert selections object to ordered array based on indices
    const orderedSelections = [];
    const maxIndex = Math.max(...Object.keys(selections).map(k => parseInt(k)));
    for (let i = 0; i <= maxIndex; i++) {
      orderedSelections[i] = selections[i]?.value || null;
    }
    
    
    const matchingVariant = productData.variants.find(variant => {
      
      const matches = variant.options.every((optionValue, index) => {
        const selectionValue = orderedSelections[index];
        const isMatch = selectionValue === optionValue;
        
        return isMatch;
      });
      
      return matches;
    });
    
    return matchingVariant;
  }

  updateOptionDisplay(option, variant, productData = null, variantMetafields = null) {
    const priceElement = option.querySelector('.select-popout__option-price');
    if (priceElement && variant.price !== undefined) {
      const formattedPrice = this.formatMoney(variant.price);
      priceElement.textContent = formattedPrice;
    }

    // Update badge using variant-specific metafields
    let existingBadge = option.querySelector('.select-popout__option-badge');
    
    // First try variant-specific metafields
    let badgeData = null;
    if (variantMetafields) {
      badgeData = this.getVariantBadgeData(variant.id, variantMetafields);
    }
    
    // Fallback to product-level metafields if no variant-specific data
    if (!badgeData && productData && productData.metafields && productData.metafields.custom && productData.metafields.custom.custom_callout_variant) {
      const metafieldData = productData.metafields.custom.custom_callout_variant.value;
      
      if (Array.isArray(metafieldData)) {
        // If metafield contains array of variant-specific data
        badgeData = metafieldData.find(item => 
          item.variant_id === variant.id || 
          item.variant_title === variant.title ||
          (item.variant_options && JSON.stringify(item.variant_options) === JSON.stringify(variant.options))
        );
      } else if (typeof metafieldData === 'object') {
        // If metafield contains a single badge object to apply to all variants
        badgeData = metafieldData;
      }
    }
    
    // Handle badge creation/removal based on badge data
    if (badgeData && badgeData.text) {
      // Create badge if it doesn't exist
      if (!existingBadge) {
        existingBadge = document.createElement('span');
        existingBadge.classList.add('select-popout__option-badge');
        
        // Find the correct container to append the badge
        const optionContent = option.querySelector('.select-popout__option-content');
        const optionMain = optionContent ? optionContent.querySelector('.select-popout__option-main') : null;
        
        if (optionMain) {
          optionMain.appendChild(existingBadge);
        } else if (optionContent) {
          optionContent.appendChild(existingBadge);
        } else {
          option.appendChild(existingBadge);
        }
      }
      
      // Update badge content and styling
      existingBadge.textContent = badgeData.text;
      if (badgeData.background_color) {
        existingBadge.style.backgroundColor = badgeData.background_color;
      }
      existingBadge.style.display = 'inline-block';
    } else if (existingBadge) {
      // Remove badge from DOM if no badge data
      existingBadge.remove();
    }
  }

  updateToggleDisplay(toggle, variant, selectedValue, optionName, productData = null, variantMetafields = null) {
    const valueContent = toggle.querySelector('.select-popout__value-content');
    if (!valueContent) return;

    // Handle price display - create if it doesn't exist
    let priceElement = valueContent.querySelector('.select-popout__value-price');
    if (variant.price !== undefined) {
      if (!priceElement) {
        priceElement = document.createElement('span');
        priceElement.classList.add('select-popout__value-price');
        
        // Find the correct container to append the price
        const valueMain = valueContent.querySelector('.select-popout__value-main');
        if (valueMain) {
          valueMain.appendChild(priceElement);
        } else {
          valueContent.appendChild(priceElement);
        }
      }
      
      const formattedPrice = this.formatMoney(variant.price);
      priceElement.textContent = formattedPrice;
      priceElement.style.display = 'inline-block';
    } else if (priceElement) {
      // Remove price element if no price data
      priceElement.remove();
    }

    const titleElement = valueContent.querySelector('.select-popout__value-title');
    if (titleElement) {
      let displayValue = selectedValue;
      if (optionName.includes('size') && displayValue.toLowerCase() === 'single ball') {
        displayValue = 'single';
      }
      titleElement.textContent = displayValue;
    }

    // Update badge using variant-specific metafields
    let badgeElement = valueContent.querySelector('.select-popout__value-badge');
    
    // First try variant-specific metafields
    let badgeData = null;
    if (variantMetafields) {
      badgeData = this.getVariantBadgeData(variant.id, variantMetafields);
    }
    
    // Fallback to product-level metafields if no variant-specific data
    if (!badgeData && productData && productData.metafields && productData.metafields.custom && productData.metafields.custom.custom_callout_variant) {
      const metafieldData = productData.metafields.custom.custom_callout_variant.value;
      
      if (Array.isArray(metafieldData)) {
        // If metafield contains array of variant-specific data
        badgeData = metafieldData.find(item => 
          item.variant_id === variant.id || 
          item.variant_title === variant.title ||
          (item.variant_options && JSON.stringify(item.variant_options) === JSON.stringify(variant.options))
        );
      } else if (typeof metafieldData === 'object') {
        // If metafield contains a single badge object to apply to all variants
        badgeData = metafieldData;
      }
    }
    
    // Handle badge creation/removal based on badge data
    if (badgeData && badgeData.text) {
      // Create badge if it doesn't exist
      if (!badgeElement) {
        badgeElement = document.createElement('span');
        badgeElement.classList.add('select-popout__value-badge');
        
        // Find the correct container to append the badge
        const valueMain = valueContent.querySelector('.select-popout__value-main');
        if (valueMain) {
          valueMain.appendChild(badgeElement);
        } else {
          valueContent.appendChild(badgeElement);
        }
      }
      
      // Update badge content and styling
      badgeElement.textContent = badgeData.text;
      if (badgeData.background_color) {
        badgeElement.style.backgroundColor = badgeData.background_color;
      }
      badgeElement.style.display = 'inline-block';
    } else if (badgeElement) {
      // Remove badge from DOM if no badge data
      badgeElement.remove();
    }

    // Force update the data-popout-text attribute if it exists
    const popoutText = toggle.querySelector('[data-popout-text]');
    if (popoutText && popoutText !== valueContent) {
      // This is for non-enhanced dropdowns, update their text too
      popoutText.textContent = selectedValue;
    }
  }

  getVariantMetafields(form) {
    // Try to find the variant metafields data script
    let metafieldsElement = form.closest(this.selectors.product)?.querySelector(this.selectors.variantMetafields);
    
    if (!metafieldsElement) {
      metafieldsElement = form.querySelector(this.selectors.variantMetafields);
    }
    
    if (!metafieldsElement) {
      const productContainer = form.closest(this.selectors.product);
      if (productContainer) {
        metafieldsElement = productContainer.querySelector(this.selectors.variantMetafields);
      }
    }
    
    if (!metafieldsElement) {
      console.log('Variant metafields element not found');
      return [];
    }
    
    try {
      const metafieldsText = metafieldsElement.textContent || metafieldsElement.innerText;
      const metafieldsData = JSON.parse(metafieldsText);
      console.log('Parsed variant metafields:', metafieldsData);
      return metafieldsData;
    } catch (e) {
      console.error('Failed to parse variant metafields:', e);
      return [];
    }
  }

  getVariantBadgeData(variantId, variantMetafields) {
    if (!variantMetafields || !Array.isArray(variantMetafields)) {
      console.log('No variant metafields available');
      return null;
    }
    
    const variantData = variantMetafields.find(item => 
      item.variant_id && item.variant_id.toString() === variantId.toString()
    );
    
    console.log(`Looking for badge data for variant ${variantId}:`, variantData);
    
    if (variantData && variantData.metafield_value) {
      // The metafield_value contains the custom_callout_variant metafield
      // Check if it exists and has content
      if (variantData.metafield_value && typeof variantData.metafield_value === 'object') {
        // If it's an object, look for text or value property
        if (variantData.metafield_value.value) {
          console.log('Found badge data in metafield_value.value:', variantData.metafield_value.value);
          const badgeData = variantData.metafield_value.value;
          // Ensure we return an object with text and background_color
          if (typeof badgeData === 'object') {
            return {
              text: badgeData.text || badgeData.value || '',
              background_color: badgeData.background_color || badgeData.backgroundColor || null
            };
          } else {
            return { text: badgeData.toString(), background_color: null };
          }
        } else if (variantData.metafield_value.text) {
          console.log('Found badge data in metafield_value.text:', variantData.metafield_value.text);
          return { 
            text: variantData.metafield_value.text,
            background_color: variantData.metafield_value.background_color || variantData.metafield_value.backgroundColor || null
          };
        }
      } else if (typeof variantData.metafield_value === 'string' && variantData.metafield_value.trim() !== '') {
        // If it's a non-empty string
        try {
          // Try to parse as JSON first
          const parsedValue = JSON.parse(variantData.metafield_value);
          console.log('Parsed badge data from JSON string:', parsedValue);
          // Ensure we return an object with text and background_color
          if (typeof parsedValue === 'object') {
            return {
              text: parsedValue.text || parsedValue.value || '',
              background_color: parsedValue.background_color || parsedValue.backgroundColor || null
            };
          } else {
            return { text: parsedValue.toString(), background_color: null };
          }
        } catch (e) {
          // If not JSON, treat as plain text
          console.log('Using badge data as plain text:', variantData.metafield_value);
          return { text: variantData.metafield_value, background_color: null };
        }
      }
    }
    
    console.log(`No badge data found for variant ${variantId}`);
    return null;
  }

  updateFormDropdownPricesWithVariant(form, selectedVariant) {
    console.log('Updating dropdown prices with selected variant:', selectedVariant);
    
    // Try multiple strategies to find the product JSON
    let productJsonElement = form.closest(this.selectors.product)?.querySelector(this.selectors.productJson);
    
    // If not found, try looking within the form itself
    if (!productJsonElement) {
      productJsonElement = form.querySelector(this.selectors.productJson);
    }
    
    // If still not found, try looking in siblings
    if (!productJsonElement) {
      const productContainer = form.closest(this.selectors.product);
      if (productContainer) {
        productJsonElement = productContainer.querySelector(this.selectors.productJson);
      }
    }

    // Alternative: Try script[type="application/json"] anywhere in the form's container
    if (!productJsonElement) {
      const container = form.closest(this.selectors.product) || form.parentElement;
      productJsonElement = container?.querySelector('script[type="application/json"]');
    }

    if (!productJsonElement) {
      console.error('Product JSON script not found');
      return;
    }

    let productData;
    try {
      const jsonText = productJsonElement.textContent || productJsonElement.innerText;
      productData = JSON.parse(jsonText);
    } catch (e) {
      console.error('Failed to parse product JSON:', e);
      return;
    }

    // Find all variants that match the selected variant except for the "Choose Ball" option
    const matchingVariants = this.findVariantsExceptChooseBall(productData, selectedVariant);
    console.log('Found matching variants (excluding choose ball differences):', matchingVariants.length);

    const selectPopouts = form.querySelectorAll(this.selectors.selectPopout);
    console.log('Found selectPopouts:', selectPopouts.length);
    
    // If no popout-select found, try alternative selectors
    if (selectPopouts.length === 0) {
      console.log('No .popout-select found, trying alternative selectors...');
      const alternativeSelectors = [
        'popout-select',
        '[data-popout-select]',
        '.select-popout',
        '[data-select-popout]',
        '.variant-input-wrap',
        '.selector-wrapper'
      ];
      
      alternativeSelectors.forEach(selector => {
        const elements = form.querySelectorAll(selector);
        console.log(`Found ${elements.length} elements with selector: ${selector}`);
        if (elements.length > 0) {
          console.log('Sample element:', elements[0]);
        }
      });
      
      // Also log all form children to see what's available
      console.log('All form children:', Array.from(form.children).map(child => ({
        tagName: child.tagName,
        className: child.className,
        id: child.id
      })));
    }
    
    selectPopouts.forEach(selectPopout => {
      const input = selectPopout.querySelector(this.selectors.optionInput);
      if (!input) return;

      const optionName = input.name.replace('options[', '').replace(']', '').toLowerCase();

      // Only update "Choose Ball" dropdowns
      if (optionName !== 'choose ball') return;

      this.updateChooseBallDropdown(selectPopout, matchingVariants, optionName, productData, form);
    });
  }

  findVariantsExceptChooseBall(productData, selectedVariant) {
    if (!selectedVariant || !selectedVariant.options) {
      return [];
    }
    // Find the index of the "Choose Ball" option using product options
    let chooseBallIndex = -1;
    if (productData.options) {
      chooseBallIndex = productData.options.findIndex(option => {
        return option && option.toLowerCase() === 'choose ball';
      });
    }
    
    // Fallback: if not found in product.options, look for it in variant option names from form
    if (chooseBallIndex === -1) {
      // Use the option index from product.options array instead of form inputs
      if (productData.options) {
        productData.options.forEach((option, index) => {
          if (option && typeof option === 'string' && option.toLowerCase() === 'choose ball' && chooseBallIndex === -1) {
            chooseBallIndex = index;
          } else if (option && option.name && option.name.toLowerCase() === 'choose ball' && chooseBallIndex === -1) {
            chooseBallIndex = index;
          }
        });
      }
    }

    if (chooseBallIndex === -1) {
      console.log('Choose Ball option index not found');
      return [];
    }

    console.log('Choose Ball option is at index:', chooseBallIndex);

    // Filter variants that match all options except the Choose Ball option
    const matchingVariants = productData.variants.filter(variant => {
      if (!variant.options || variant.options.length !== selectedVariant.options.length) {
        return false;
      }

      // Check if all options except Choose Ball match
      return variant.options.every((option, index) => {
        if (index === chooseBallIndex) {
          return true; // Skip Choose Ball option comparison
        }
        return option === selectedVariant.options[index];
      });
    });

    return matchingVariants;
  }

  updateChooseBallDropdown(selectPopout, matchingVariants, optionName, productData, form) {
    // Get variant metafields for badge data
    const variantMetafields = this.getVariantMetafields(form);
    
    const options = selectPopout.querySelectorAll(this.selectors.option);
    
    options.forEach(option => {
      const selectedValue = option.dataset.value;
      if (!selectedValue) return;

      // Find the variant that corresponds to this option value
      const correspondingVariant = matchingVariants.find(variant => {
        return variant.options && variant.options.some(opt => opt === selectedValue);
      });

      if (correspondingVariant) {
        console.log(`Updating option "${selectedValue}" with variant:`, correspondingVariant.id);
        this.updateOptionDisplay(option, correspondingVariant, productData, variantMetafields);
      }
    });

    // Also update the current toggle display if there's a selected value
    const selectedOption = selectPopout.querySelector(`${this.selectors.option}[aria-selected="true"]`);
    if (selectedOption) {
      const selectedValue = selectedOption.dataset.value;
      const correspondingVariant = matchingVariants.find(variant => {
        return variant.options && variant.options.some(opt => opt === selectedValue);
      });

      if (correspondingVariant) {
        const popoutToggle = selectPopout.querySelector(this.selectors.popoutToggle);
        if (popoutToggle) {
          this.updateToggleDisplay(popoutToggle, correspondingVariant, selectedValue, optionName, productData, variantMetafields);
        }
      }
    }
  }

  formatMoney(cents) {
    // Use theme's money formatting if available
    if (window.theme && window.theme.formatMoney) {
      return window.theme.formatMoney(cents, window.theme.moneyFormat || '${{amount}}');
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new EnhancedSelectPopout();
  });
} else {
  new EnhancedSelectPopout();
}

// Also initialize on section load for dynamic sections
document.addEventListener('shopify:section:load', () => {
  new EnhancedSelectPopout();
});